/**
 * The "Products & partners" column of the Company pulse — pure summaries.
 *
 * Three CRM surfaces are condensed onto the landing page: licences that are
 * about to stop working, reseller applications waiting on a decision, and the
 * newest shipped version of each product. Each card reads the SAME BFF
 * endpoint its CRM page reads (so the server's permission gate is the one
 * that decides), and these functions turn that list into what the card shows.
 *
 * Pure and in their own module so the rules — which licence counts as
 * "expiring", which application is "ready to decide", which releases are the
 * newest — are pinned by `watch.test.ts` without mounting a component.
 */
import {
  LICENCE_EXPIRY_WARNING_DAYS,
  RESELLER_APPLICATION_STATUS_LABELS,
  type Licence,
  type ResellerApplication,
} from "@xop/dashboard-shared";

import { groupIntoFamilies, versionsOf } from "@crm-src/pages/releases/release-families";
import { releasePath } from "@crm-src/pages/releases/release-paths";
import type { ReleaseDTO } from "@crm-src/pages/releases/releases-api";

/** Rows a watch card lists before "View all". Same cap as the pulse queues. */
export const WATCH_ROW_MAX = 5;

/** A licence inside this many days is amber; beyond it, up to the shared
 * 60-day warning window, it is listed but neutral. */
export const LICENCE_SOON_DAYS = 30;

/** How long an EXPIRED licence stays on the card. One that lapsed a year ago
 * and was never renewed is history, not something to act on today. */
export const LICENCE_EXPIRED_GRACE_DAYS = 30;

/** Whole days from `today` (local midnight) to an ISO date. Negative = past. */
export function daysUntil(isoDate: string, today: Date = new Date()): number {
  const start = new Date(today);
  start.setHours(0, 0, 0, 0);
  const end = new Date(`${isoDate.slice(0, 10)}T00:00:00`);
  return Math.round((end.getTime() - start.getTime()) / 86_400_000);
}

/* ── Licences ─────────────────────────────────────────────────────────── */

export interface LicenceWatchRow {
  id: number;
  customerName: string;
  label: string;
  internal: boolean;
  expiresOn: string;
  daysLeft: number;
  tone: "bad" | "warn" | "neutral";
}

export interface LicenceWatch {
  /** Lapsed within the last {@link LICENCE_EXPIRED_GRACE_DAYS} days. */
  expired: number;
  /** Today up to {@link LICENCE_SOON_DAYS} days out. */
  soon: number;
  /** Beyond that, up to the shared {@link LICENCE_EXPIRY_WARNING_DAYS}. */
  later: number;
  /** Most urgent first (the longest-lapsed at the top), capped. */
  rows: LicenceWatchRow[];
  /** Parked for CTO approval, oldest request first. Rendered ONLY for a
   * super admin — approving is their act and nobody else's. */
  pendingApproval: Array<Pick<Licence, "id" | "customerName" | "label">>;
}

export function summariseLicences(licences: readonly Licence[], today: Date = new Date()): LicenceWatch {
  const watched: LicenceWatchRow[] = [];
  let expired = 0;
  let soon = 0;
  let later = 0;

  for (const licence of licences) {
    // Only an ISSUED licence is running on a box. A revoked one already
    // stopped on purpose; a perpetual one never stops.
    if (licence.status !== "issued" || !licence.expiresOn) continue;
    const daysLeft = daysUntil(licence.expiresOn, today);
    if (daysLeft < -LICENCE_EXPIRED_GRACE_DAYS || daysLeft > LICENCE_EXPIRY_WARNING_DAYS) continue;

    let tone: LicenceWatchRow["tone"];
    if (daysLeft < 0) {
      expired += 1;
      tone = "bad";
    } else if (daysLeft <= LICENCE_SOON_DAYS) {
      soon += 1;
      tone = "warn";
    } else {
      later += 1;
      tone = "neutral";
    }
    watched.push({
      id: licence.id,
      customerName: licence.customerName,
      label: licence.label,
      internal: licence.customerInternal,
      expiresOn: licence.expiresOn,
      daysLeft,
      tone,
    });
  }

  const pendingApproval = licences
    .filter((l) => l.approval?.status === "pending")
    .sort((a, b) => (a.approval.requestedAt ?? "").localeCompare(b.approval.requestedAt ?? ""))
    .map(({ id, customerName, label }) => ({ id, customerName, label }));

  return {
    expired,
    soon,
    later,
    rows: watched.sort((a, b) => a.daysLeft - b.daysLeft).slice(0, WATCH_ROW_MAX),
    pendingApproval,
  };
}

/** "Expired 3 days ago" · "Expires today" · "In 12 days". */
export function expiryLabel(daysLeft: number): string {
  if (daysLeft < 0) {
    const ago = -daysLeft;
    return `Expired ${ago} day${ago === 1 ? "" : "s"} ago`;
  }
  if (daysLeft === 0) return "Expires today";
  if (daysLeft === 1) return "Tomorrow";
  return `In ${daysLeft} days`;
}

/* ── Reseller applications ────────────────────────────────────────────── */

export interface ApplicationWatchRow {
  id: number;
  companyName: string;
  status: ResellerApplication["status"];
  statusLabel: string;
  /** Days since the application last moved (documents in, or registered). */
  waitingDays: number;
  ready: boolean;
}

export interface ApplicationWatch {
  /** Signed documents are in — only a decision is left. */
  ready: number;
  /** Registered, still waiting on the applicant's signed documents. */
  awaitingDocuments: number;
  /** Ready ones first, then whoever has waited longest. */
  rows: ApplicationWatchRow[];
}

export function summariseApplications(
  applications: readonly ResellerApplication[],
  today: Date = new Date(),
): ApplicationWatch {
  const open = applications.filter((a) => a.status === "submitted" || a.status === "documents_submitted");
  const rows = open
    .map((a) => {
      const ready = a.status === "documents_submitted";
      const since = (ready ? a.documents_submitted_at : null) ?? a.created_at;
      return {
        id: a.id,
        companyName: a.company_name,
        status: a.status,
        statusLabel: RESELLER_APPLICATION_STATUS_LABELS[a.status],
        waitingDays: Math.max(0, -daysUntil(since, today)),
        ready,
      };
    })
    .sort((a, b) => Number(b.ready) - Number(a.ready) || b.waitingDays - a.waitingDays);

  return {
    ready: rows.filter((r) => r.ready).length,
    awaitingDocuments: rows.filter((r) => !r.ready).length,
    rows: rows.slice(0, WATCH_ROW_MAX),
  };
}

/** "Today" · "1 day" · "6 days". */
export function waitingLabel(days: number): string {
  if (days <= 0) return "Today";
  return `${days} day${days === 1 ? "" : "s"}`;
}

/* ── Releases ─────────────────────────────────────────────────────────── */

/** How many release folders the card offers as quick links. */
export const RELEASE_TILE_MAX = 3;

export interface ReleaseTile {
  id: number;
  /** The RELEASE number printed on the folder (11, 10.2). */
  release: string;
  status: string;
  /** The newest VERSION inside it — null for a release with none yet. */
  newestVersion: string | null;
  newestReleasedOn: string | null;
  versionCount: number;
  /** The release's own page, `/crm/releases/<release>`. */
  href: string;
}

/**
 * The newest release folders, as quick links into their pages.
 *
 * Built on `groupIntoFamilies` — the same nesting and the same order as the
 * CRM ▸ Releases folder grid (by the newest version each release holds) — so
 * the card's first tile is always that page's first folder, and a version
 * whose release is missing still stands in a folder of its own name.
 */
export function recentReleases(rows: readonly ReleaseDTO[], limit: number = RELEASE_TILE_MAX): ReleaseTile[] {
  return groupIntoFamilies(rows)
    .slice(0, limit)
    .map((family) => {
      const newest = family.hasLine ? family.builds[0] : family.line;
      return {
        id: family.line.id,
        release: family.line.version,
        status: family.line.status,
        newestVersion: newest?.version ?? null,
        newestReleasedOn: newest?.release_date ?? null,
        versionCount: versionsOf(family).length,
        href: releasePath(family.line.version),
      };
    });
}

/** "12 Sep 2026" for an ISO date, or null. Parsed as a LOCAL date so a
 * release dated the 12th never prints as the 11th west of UTC. */
export function shortDate(isoDate: string | null): string | null {
  if (!isoDate) return null;
  const d = new Date(`${isoDate.slice(0, 10)}T00:00:00`);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
}
